import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import config from "../config";
import "./WebsiteReport.css";

const ALL_STAGES = [
  { key: "http_analysis", label: "HTTP Analysis", group: "Core", alwaysOn: true },
  { key: "scrape", label: "Page Content Scrape", group: "Core", alwaysOn: true },
  { key: "readability", label: "Readability Analysis", group: "Content" },
  { key: "pagespeed", label: "PageSpeed Insights", group: "Performance", apiKey: "GOOGLE_API_KEY" },
  { key: "crux", label: "Chrome UX Report", group: "Performance", apiKey: "GOOGLE_API_KEY" },
  { key: "domain_info", label: "Domain Information", group: "Infrastructure" },
  { key: "screenshot", label: "Website Screenshot", group: "Visual", apiKey: "SCREENSHOT_API_KEY" },
  { key: "accessibility", label: "Accessibility (WAVE)", group: "Content", apiKey: "WAVE_API_KEY" },
  { key: "js_vulnerabilities", label: "JS Library Vulnerabilities", group: "Security" },
  { key: "http_methods", label: "Exposed HTTP Methods", group: "Security" },
  { key: "url_reputation", label: "URL Reputation", group: "Security", apiKey: "VIRUSTOTAL_API_KEY" },
  { key: "domain_issues", label: "Domain Issues Analysis", group: "Infrastructure" },
  { key: "ai_analysis", label: "AI Analysis", group: "Summary" },
];

const DEFAULT_ENABLED = ALL_STAGES.map((s) => s.key);

const GROUPS = [...new Set(ALL_STAGES.map((s) => s.group))];

const WebsiteReport = () => {
  const [url, setUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [reports, setReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [enabledStages, setEnabledStages] = useState(DEFAULT_ENABLED);
  const [showConfig, setShowConfig] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      setIsLoadingReports(true);
      const resp = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.websiteReport.reports}`,
        { credentials: "include" }
      );
      const data = await resp.json();
      if (data.success) {
        setReports(data.reports);
      }
    } catch (err) {
      console.error("Error fetching reports:", err);
    } finally {
      setIsLoadingReports(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    const hasRunning = reports.some((r) => r.status === "running");
    if (!hasRunning) return;
    const interval = setInterval(fetchReports, 5000);
    return () => clearInterval(interval);
  }, [reports, fetchReports]);

  const toggleStage = (key) => {
    const stage = ALL_STAGES.find((s) => s.key === key);
    if (stage?.alwaysOn) return;
    setEnabledStages((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleGroup = (group) => {
    const groupStages = ALL_STAGES.filter(
      (s) => s.group === group && !s.alwaysOn
    );
    const allEnabled = groupStages.every((s) =>
      enabledStages.includes(s.key)
    );
    if (allEnabled) {
      setEnabledStages((prev) =>
        prev.filter((k) => !groupStages.find((s) => s.key === k))
      );
    } else {
      setEnabledStages((prev) => [
        ...prev,
        ...groupStages.map((s) => s.key).filter((k) => !prev.includes(k)),
      ]);
    }
  };

  const selectAll = () => setEnabledStages(DEFAULT_ENABLED);
  const selectNone = () =>
    setEnabledStages(ALL_STAGES.filter((s) => s.alwaysOn).map((s) => s.key));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;

    setIsSubmitting(true);
    setError("");

    try {
      const resp = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.websiteReport.start}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: url.trim(),
            enabled_stages: enabledStages,
          }),
        }
      );
      const data = await resp.json();
      if (data.success) {
        setUrl("");
        fetchReports();
      } else {
        setError(data.error || "Failed to start report");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (token) => {
    if (!window.confirm("Delete this report?")) return;
    try {
      await fetch(
        `${config.api.baseUrl}${config.api.endpoints.websiteReport.reports}/${token}`,
        { method: "DELETE", credentials: "include" }
      );
      fetchReports();
    } catch (err) {
      console.error("Error deleting report:", err);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return "";
    const letter = grade.charAt(0).toUpperCase();
    if (letter === "A") return "wr-grade-a";
    if (letter === "B") return "wr-grade-b";
    if (letter === "C") return "wr-grade-c";
    return "wr-grade-d";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "\u2014";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const enabledCount = enabledStages.length;
  const totalCount = ALL_STAGES.length;

  return (
    <Layout>
      <div className="wr-container">
        <div className="wr-header">
          <h1>Website Report</h1>
          <p className="wr-subtitle">
            Generate comprehensive website analysis reports covering
            performance, security, SEO, and more.
          </p>
          <a
            href="/ai-report/sample"
            target="_blank"
            rel="noopener noreferrer"
            className="wr-action-btn wr-view-btn"
            style={{ display: "inline-block", marginTop: 8 }}
          >
            View Sample Report
          </a>
        </div>

        <form className="wr-form" onSubmit={handleSubmit}>
          <div className="wr-input-group">
            <input
              type="text"
              className="wr-url-input"
              placeholder="Enter website URL (e.g., example.com)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isSubmitting}
            />
            <button
              type="submit"
              className="wr-submit-btn"
              disabled={isSubmitting || !url.trim()}
            >
              {isSubmitting ? "Starting..." : "Generate Report"}
            </button>
          </div>

          <div className="wr-config-toggle-row">
            <button
              type="button"
              className="wr-config-toggle"
              onClick={() => setShowConfig((v) => !v)}
            >
              {showConfig ? "Hide" : "Configure"} Sections
              <span className="wr-config-count">
                {enabledCount}/{totalCount}
              </span>
              <span
                className={`wr-config-chevron ${showConfig ? "wr-config-chevron-open" : ""}`}
              >
                &#9662;
              </span>
            </button>
          </div>

          {showConfig && (
            <div className="wr-stage-config">
              <div className="wr-config-actions">
                <button
                  type="button"
                  className="wr-config-action-btn"
                  onClick={selectAll}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="wr-config-action-btn"
                  onClick={selectNone}
                >
                  Select None
                </button>
              </div>

              {GROUPS.map((group) => {
                const groupStages = ALL_STAGES.filter(
                  (s) => s.group === group
                );
                const toggleable = groupStages.filter((s) => !s.alwaysOn);
                const allEnabled =
                  toggleable.length > 0 &&
                  toggleable.every((s) => enabledStages.includes(s.key));
                const someEnabled =
                  toggleable.some((s) => enabledStages.includes(s.key)) &&
                  !allEnabled;

                const allAlwaysOn = groupStages.every((s) => s.alwaysOn);

                return (
                  <div key={group} className="wr-stage-group">
                    <div
                      className={`wr-group-header ${allAlwaysOn ? "wr-group-header-locked" : ""}`}
                      onClick={() => !allAlwaysOn && toggleGroup(group)}
                    >
                      {!allAlwaysOn && (
                        <span
                          className={`wr-group-checkbox ${allEnabled ? "wr-checked" : someEnabled ? "wr-indeterminate" : ""}`}
                        />
                      )}
                      <span className="wr-group-label">{group}</span>
                      {allAlwaysOn && (
                        <span className="wr-stage-required">Required</span>
                      )}
                    </div>
                    <div className="wr-group-stages">
                      {groupStages.map((stage) => (
                        <label
                          key={stage.key}
                          className={`wr-stage-toggle ${stage.alwaysOn ? "wr-stage-locked" : ""}`}
                        >
                          {!stage.alwaysOn && (
                            <>
                              <input
                                type="checkbox"
                                checked={enabledStages.includes(stage.key)}
                                onChange={() => toggleStage(stage.key)}
                              />
                              <span className="wr-stage-checkbox" />
                            </>
                          )}
                          <span className="wr-stage-label-text">
                            {stage.label}
                          </span>
                          {stage.apiKey && (
                            <span className="wr-stage-api-badge">API Key</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {error && <div className="wr-error">{error}</div>}
        </form>

        <div className="wr-reports-section">
          <h2>Your Reports</h2>
          {isLoadingReports && reports.length === 0 ? (
            <div className="wr-loading">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="wr-empty">
              No reports yet. Enter a URL above to generate your first report.
            </div>
          ) : (
            <div className="wr-table-wrapper">
              <table className="wr-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Grade</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.report_token}>
                      <td className="wr-domain-cell">{report.domain}</td>
                      <td>
                        {report.overall_grade ? (
                          <span
                            className={`wr-grade-badge ${getGradeColor(report.overall_grade)}`}
                          >
                            {report.overall_grade}
                          </span>
                        ) : (
                          "\u2014"
                        )}
                      </td>
                      <td>
                        {report.status === "running" ? (
                          <div className="wr-progress-cell">
                            <div className="wr-progress-bar">
                              <div
                                className="wr-progress-fill"
                                style={{
                                  width: `${((report.progress?.stages_completed || 0) / (report.progress?.stages_total || 17)) * 100}%`,
                                }}
                              />
                            </div>
                            <span className="wr-progress-label">
                              {report.progress?.current_stage_label ||
                                "Starting..."}
                            </span>
                          </div>
                        ) : report.status === "completed" ? (
                          <span className="wr-status-completed">Completed</span>
                        ) : (
                          <span className="wr-status-error">Error</span>
                        )}
                      </td>
                      <td className="wr-date-cell">
                        {formatDate(report.created_at)}
                      </td>
                      <td className="wr-actions-cell">
                        {(report.status === "completed" ||
                          report.status === "running") && (
                          <a
                            href={`/ai-report/${report.report_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wr-action-btn wr-view-btn"
                          >
                            View
                          </a>
                        )}
                        {report.status === "completed" && (
                          <a
                            href={`/ai-report/${report.report_token}?download=1`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wr-action-btn wr-download-btn"
                            title="Download PDF (opens report in a new tab and saves automatically)"
                          >
                            Download
                          </a>
                        )}
                        <button
                          className="wr-action-btn wr-delete-btn"
                          onClick={() => handleDelete(report.report_token)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WebsiteReport;
